import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile"
import { motion } from "motion/react"
import { type FC, useRef, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router"

import { academyNameTrademarkNotice, getAcademyNameValidationError } from "@forento/core/utilities/academyName"
import { getPasswordValidationError, passwordRequirementsText } from "@forento/core/utilities/password"
import Form from "@forento/ui/components/Form"

import { SubmitButton } from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import { AddIcon } from "~/components/Icon"
import InputField from "~/components/InputField"
import Link from "~/components/Link"
import { useAlert } from "~/contexts/AlertContext"
import { useBrand } from "~/contexts/BrandContext"
import { useDisableGoogleSignIn } from "~/contexts/GoogleSignInContext"
import { useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

import Layout, { Logo } from "../Layout"
import { authCardClass, staggerContainer, staggerItem } from "../Layout/authCard"
import appSumoLogo from "./appSumoLogo.svg"

const AppSumoSignupPage: FC = () => {
	const params = useParams()
	const keyFromParams = params.appSumoKey!.trim().toUpperCase()

	const brand = useBrand()
	const alert = useAlert()
	const user = useUser()
	const navigate = useNavigate()
	useDisableGoogleSignIn()

	const keyFromAPI = query.user.getAppSumoKey.useQuery(keyFromParams)

	const turnstileRef = useRef<TurnstileInstance | null>(null)

	const [keys, setKeys] = useState<string[]>([keyFromParams])
	const [newKey, setNewKey] = useState<string>("")
	const [isCheckingNewKey, setCheckingNewKey] = useState(false)
	const [firstName, setFirstName] = useState("")
	const [lastName, setLastName] = useState("")
	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [passwordConfirmation, setPasswordConfirmation] = useState("")
	const [platformName, setPlatformName] = useState("")
	const [isAccountEmailChecked, setAccountEmailChecked] = useState(true)
	const [contactEmail, setContactEmail] = useState("")

	const [error, setError] = useState<string>()
	const [isSubmitting, setSubmitting] = useState(false)

	const handleSignup = async () => {
		const captchaToken = turnstileRef.current?.getResponse()
		if (!captchaToken) {
			setError("Please complete the captcha verification.")
			return
		}
		setSubmitting(true)
		setError(undefined)
		if (password !== passwordConfirmation) {
			setError("Passwords don't match.")
			setSubmitting(false)
			return
		}
		const passwordError = getPasswordValidationError(password)
		if (passwordError !== null) {
			setError(passwordError)
			setSubmitting(false)
			return
		}
		const body = {
			firstName: firstName.trim(),
			lastName: lastName.trim(),
			email: email.trim(),
			password,
			platformName: platformName.trim(),
			contactEmail: isAccountEmailChecked ? email.trim() : contactEmail.trim(),
			appSumoKeys: keys,
			captchaToken,
		}
		if ([body.firstName, body.lastName, body.password, body.email, body.platformName].some(x => x.length === 0)) {
			setError("Please fill in all fields.")
			setSubmitting(false)
			return
		}
		const academyNameError = getAcademyNameValidationError(body.platformName)
		if (academyNameError !== null) {
			setError(academyNameError)
			setSubmitting(false)
			return
		}
		try {
			const response = await trpc.user.registerCreatorAppSumo.mutate(body)
			if (response.status === "email-taken") {
				setError("That email address is already associated with an account.")
				setSubmitting(false)
				turnstileRef.current?.reset()
				return
			}
			if (response.status === "invalid-key" || response.status === "version-mismatch") {
				setError(
					response.status === "invalid-key"
						? "One of your AppSumo keys is invalid or already taken."
						: "These AppSumo keys can't be combined during sign up. Please contact customer support for assistance.",
				)
				setSubmitting(false)
				turnstileRef.current?.reset()
				return
			}
			await user.reload({ clearSessionCache: true })
			navigate(routes.account.createPlatform())
		} catch {
			setError("Something went wrong. Please try again later.")
			setSubmitting(false)
			turnstileRef.current?.reset()
		}
	}

	const handleAddKey = async () => {
		setCheckingNewKey(true)
		const normalizedKey = newKey.trim().toUpperCase()
		if (keys.includes(normalizedKey)) {
			await alert.show("Key already added", "That key is already added.")
			setCheckingNewKey(false)
			return
		}
		try {
			const response = await trpc.user.getAppSumoKey.query(normalizedKey)
			if (response === null) {
				alert.show("Invalid key", "That key is invalid, or already taken.")
				setCheckingNewKey(false)
				setCheckingNewKey(false)
				return
			}
			if (response.version !== keyFromAPI.data?.version) {
				alert.show(
					"Invalid key",
					"This key can't be added to your account during sign up. Please contact customer support for assistance.",
				)
				setCheckingNewKey(false)
				setCheckingNewKey(false)
				return
			}
			setKeys(current => [...current, normalizedKey])
			setNewKey("")
		} catch (err) {
			console.error(err)
			setCheckingNewKey(false)
		}
		setCheckingNewKey(false)
	}

	if (brand.id !== "forento") return <Navigate replace to={routes.account.signup()} />

	const isLoading = keyFromAPI.data === undefined
	const isInvalid = keyFromAPI.data === null

	if (isLoading) {
		return (
			<Layout>
				<Logo />
				<div className={authCardClass}>
					<div className="flex flex-col items-center gap-4 py-8">
						<p className="text-sm text-zinc-500">Verifying your key...</p>
					</div>
				</div>
			</Layout>
		)
	}

	if (isInvalid) {
		return (
			<Layout>
				<Logo />
				<div className={authCardClass}>
					<motion.div
						className="flex w-full flex-col items-stretch gap-0"
						variants={staggerContainer}
						initial="hidden"
						animate="visible"
					>
						<motion.div variants={staggerItem} className="flex items-center gap-3">
							<img src={appSumoLogo} alt="AppSumo" className="h-6 w-auto" />
							<h1 className="text-left text-xl font-bold tracking-tight text-zinc-900">
								Register via AppSumo
							</h1>
						</motion.div>
						<motion.p
							variants={staggerItem}
							className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700"
						>
							That link is either invalid or already used.
						</motion.p>
					</motion.div>
				</div>
			</Layout>
		)
	}

	return (
		<Layout>
			<Logo />
			<div className={authCardClass}>
				<Form onSubmit={handleAddKey}>
					<motion.div
						className="flex w-full flex-col items-stretch gap-0"
						variants={staggerContainer}
						initial="hidden"
						animate="visible"
					>
						<motion.div variants={staggerItem} className="flex items-center gap-3">
							<img src={appSumoLogo} alt="AppSumo" className="h-6 w-auto" />
							<h1 className="text-left text-xl font-bold tracking-tight text-zinc-900">
								Register via AppSumo
							</h1>
						</motion.div>
						<motion.p variants={staggerItem} className="mt-1.5 text-left text-sm text-zinc-500">
							Add your AppSumo code(s) below, then complete your account.
						</motion.p>

						<motion.div variants={staggerItem} className="mt-6">
							<label className="block text-sm font-medium text-zinc-700">Redeemed AppSumo codes</label>
							<ol className="mt-2 list-inside list-decimal space-y-1 text-sm text-zinc-600">
								{keys.map(k => (
									<li key={k}>
										<code className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-zinc-800">
											{k}
										</code>
									</li>
								))}
							</ol>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4 flex items-end gap-2">
							<div className="min-w-0 flex-1">
								<InputField
									label="Add AppSumo codes"
									value={newKey}
									onChange={setNewKey}
									placeholder="Enter code"
									compact
								/>
							</div>
							<SubmitButton
								variant="primary"
								isLoading={isCheckingNewKey}
								replaceOnLoading
								className="size-10 shrink-0 cursor-pointer rounded-xl p-0"
							>
								<AddIcon className="size-5" />
							</SubmitButton>
						</motion.div>
					</motion.div>
				</Form>

				<hr className="my-8 border-zinc-200" />

				<Form onSubmit={handleSignup}>
					<motion.div
						className="flex w-full flex-col items-stretch gap-0"
						variants={staggerContainer}
						initial="hidden"
						animate="visible"
					>
						<motion.div variants={staggerItem}>
							<h2 className="text-left text-xl font-bold tracking-tight text-zinc-900">
								Create your account
							</h2>
							<p className="mt-1.5 text-left text-sm text-zinc-500">Complete your details below.</p>
						</motion.div>

						{error !== undefined && (
							<motion.p
								variants={staggerItem}
								className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700"
							>
								{error}
							</motion.p>
						)}

						<motion.div variants={staggerItem} className="mt-6 grid grid-cols-2 gap-3">
							<InputField
								label="First name"
								value={firstName}
								onChange={setFirstName}
								autoComplete="given-name"
							/>
							<InputField
								label="Last name"
								value={lastName}
								onChange={setLastName}
								autoComplete="family-name"
							/>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<InputField
								inputType="email"
								label="Email"
								value={email}
								onChange={setEmail}
								autoComplete="email"
							/>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<InputField
								inputType="password"
								label="Password"
								value={password}
								onChange={setPassword}
								autoComplete="new-password"
							/>
							<p className="mt-2 text-left text-xs leading-relaxed text-zinc-500">
								{passwordRequirementsText}
							</p>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<InputField
								inputType="password"
								label="Confirm password"
								value={passwordConfirmation}
								onChange={setPasswordConfirmation}
								autoComplete="new-password"
							/>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<InputField label="Academy name" value={platformName} onChange={setPlatformName} />
							<p className="mt-2 text-left text-xs leading-relaxed text-zinc-500">
								{academyNameTrademarkNotice}
							</p>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<Checkbox
								label="Use my account's email address"
								isChecked={isAccountEmailChecked}
								onChange={setAccountEmailChecked}
								className="mb-2"
							/>
							{isAccountEmailChecked ? (
								<InputField label="Contact email address" value={email} disabled />
							) : (
								<InputField
									label="Contact email address"
									placeholder="e.g. contact@davescourses.com"
									value={contactEmail}
									onChange={setContactEmail}
								/>
							)}
							<p className="mt-2 text-xs leading-relaxed text-zinc-500">
								The contact email will be public to your users and used in your terms of service.
							</p>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<Turnstile
								ref={turnstileRef}
								siteKey={CONFIG.turnstileSiteKey}
								className="flex justify-center"
							/>
						</motion.div>

						<motion.p
							variants={staggerItem}
							className="mt-5 text-left text-xs leading-relaxed text-zinc-500"
						>
							By signing up you agree to our{" "}
							<a
								href="https://forento.io/policy/terms-of-service"
								target="_blank"
								rel="noreferrer noopener"
								className="text-zinc-600 hover:text-zinc-900"
							>
								Terms of Service
							</a>
							.
						</motion.p>

						<motion.div variants={staggerItem} className="mt-5">
							<SubmitButton
								variant="primary"
								isLoading={isSubmitting}
								className="w-full cursor-pointer rounded-xl py-3 text-sm font-semibold"
							>
								Sign up
							</SubmitButton>
						</motion.div>

						<motion.p variants={staggerItem} className="mt-5 text-center text-sm text-zinc-500">
							Already have an account?{" "}
							<Link
								to={routes.account.signin()}
								className="text-primary hover:text-primary/80 font-medium transition-colors"
							>
								Sign in
							</Link>
						</motion.p>
					</motion.div>
				</Form>
			</div>
		</Layout>
	)
}

export default AppSumoSignupPage
